import { Injectable, signal, computed, WritableSignal, Signal } from '@angular/core';
import { Router } from '@angular/router';

/**
 * BASE WORKFLOW STATE MANAGEMENT SERVICE
 * ======================================
 *
 * PURPOSE:
 * This is the foundation for managing multi-step workflows in the application.
 * Think of it like a GPS system - it knows all possible destinations (states),
 * the rules for moving between them (transitions), and can tell you where you
 * currently are and where you can go next.
 *
 * WHY WE NEED THIS:
 * Before: Navigation logic was scattered across components with lots of if-else statements
 * After: All workflow logic is centralized, making it easier to understand and maintain
 *
 * ANALOGY:
 * Imagine a board game where each square has rules about which squares you can move to next.
 * Instead of writing those rules on every square (component), we write them once in a
 * rulebook (this service). Players (users) just need to ask "where can I go from here?"
 *
 * HOW TO USE:
 * 1. Extend this class in your module-specific workflow service
 * 2. Define your workflow states (the steps in your process)
 * 3. Define transition rules (when can you move from step A to step B?)
 * 4. Call navigateToState() to move through the workflow
 */

/**
 * Represents a single step/page in a workflow
 */
export interface WorkflowState {
  /** Unique identifier for this state (e.g., 'otp', 'submit', 'welcome') */
  name: string;

  /** The URL path for this state (e.g., '/quick-apply/otp') */
  path: string;

  /** Optional: A human-readable label for this state */
  label?: string;

  /** Optional: Any additional metadata needed for this state */
  metadata?: Record<string, any>;
}

/**
 * Defines when you can move from one state to another
 * This is like saying: "You can only enter the final exam room if you've completed all assignments"
 */
export interface TransitionRule {
  /** The state you're trying to leave */
  fromState: string;

  /** The state you're trying to reach */
  toState: string;

  /** A function that returns true if this transition is allowed */
  canTransition: (context?: any) => boolean;

  /** Optional: Reason why transition might be blocked (for debugging/error messages) */
  blockReason?: string;
}

/**
 * Tracks the history of where the user has been in the workflow
 */
export interface WorkflowHistory {
  state: string;
  timestamp: Date;
  metadata?: Record<string, any>;
}

@Injectable()
export abstract class BaseWorkflowStateService {

  // ============================================================================
  // SIGNALS - THE REACTIVE STATE MANAGEMENT
  // ============================================================================
  // Signals are Angular's new reactive primitives. Think of them like smart variables
  // that automatically notify components when they change.

  /**
   * The current state the user is in
   * This is a WritableSignal, meaning we can change its value directly
   */
  protected _currentState: WritableSignal<WorkflowState | null> = signal(null);

  /**
   * A public read-only version of the current state
   * Components can read this but can't modify it directly - they must go through
   * the workflow service to change states
   */
  public currentState: Signal<WorkflowState | null> = this._currentState.asReadonly();

  /**
   * History of all states the user has visited
   * Useful for "back" buttons or analytics
   */
  protected _stateHistory: WritableSignal<WorkflowHistory[]> = signal([]);
  public stateHistory: Signal<WorkflowHistory[]> = this._stateHistory.asReadonly();

  /**
   * Whether the workflow is currently in a loading state
   * (e.g., validating a transition or fetching data)
   */
  protected _isLoading: WritableSignal<boolean> = signal(false);
  public isLoading: Signal<boolean> = this._isLoading.asReadonly();

  /**
   * Error message if a transition fails
   */
  protected _error: WritableSignal<string | null> = signal(null);
  public error: Signal<string | null> = this._error.asReadonly();

  // ============================================================================
  // COMPUTED SIGNALS - DERIVED STATE
  // ============================================================================
  // Computed signals automatically recalculate when their dependencies change
  // Think of them like Excel formulas that update when cells they reference change

  /**
   * Gets the previous state from history
   * Useful for implementing "back" buttons
   */
  public previousState: Signal<WorkflowState | null> = computed(() => {
    const history = this._stateHistory();
    if (history.length < 2) return null;

    const previousHistoryEntry = history[history.length - 2];
    return this.getStateByName(previousHistoryEntry.state);
  });

  /**
   * Checks if the user can go back to the previous state
   */
  public canGoBack: Signal<boolean> = computed(() => {
    return this._stateHistory().length > 1;
  });

  /**
   * Gets the current state name as a string (convenience getter)
   */
  public currentStateName: Signal<string | null> = computed(() => {
    return this._currentState()?.name ?? null;
  });

  // ============================================================================
  // ABSTRACT METHODS - MUST BE IMPLEMENTED BY CHILD CLASSES
  // ============================================================================
  // These define the contract that each module-specific workflow service must fulfill

  /**
   * Returns all possible states in this workflow
   * Each module defines its own states
   */
  protected abstract getWorkflowStates(): WorkflowState[];

  /**
   * Returns all transition rules for this workflow
   * Each module defines its own rules
   */
  protected abstract getTransitionRules(): TransitionRule[];

  /**
   * Returns the initial/starting state for this workflow
   */
  protected abstract getInitialState(): WorkflowState;

  // ============================================================================
  // CONSTRUCTOR
  // ============================================================================

  constructor(protected router: Router) {
    // Initialize the workflow with the starting state
    this._currentState.set(this.getInitialState());
  }

  // ============================================================================
  // PUBLIC NAVIGATION METHODS
  // ============================================================================

  /**
   * Navigate to a specific state in the workflow
   *
   * This is the main method components call to move through the workflow
   * It checks if the transition is allowed before actually navigating
   *
   * @param stateName - The name of the state to navigate to
   * @param context - Optional context data for validation (e.g., user data, form values)
   * @param options - Router navigation options (e.g., skipLocationChange, queryParams)
   * @returns Promise<boolean> - true if navigation succeeded, false otherwise
   *
   * EXAMPLE:
   * await workflowService.navigateToState('submit', { hasCompletedForm: true });
   */
  public async navigateToState(
    stateName: string,
    context?: any,
    options?: { skipLocationChange?: boolean; queryParams?: any; relativeTo?: any; queryParamsHandling?: any }
  ): Promise<boolean> {

    // Clear any previous errors
    this._error.set(null);

    // Find the target state
    const targetState = this.getStateByName(stateName);
    if (!targetState) {
      const errorMsg = `State "${stateName}" not found in workflow`;
      console.error(errorMsg);
      this._error.set(errorMsg);
      return false;
    }

    // Check if this transition is allowed
    const currentStateName = this._currentState()?.name;
    if (currentStateName) {
      const canTransition = this.canTransitionTo(currentStateName, stateName, context);
      if (!canTransition) {
        const rule = this.getTransitionRule(currentStateName, stateName);
        const errorMsg = rule?.blockReason || `Cannot transition from ${currentStateName} to ${stateName}`;
        console.warn(errorMsg);
        this._error.set(errorMsg);
        return false;
      }
    }

    // Set loading state
    this._isLoading.set(true);

    try {
      // Perform the navigation
      const navigationSucceeded = await this.router.navigate([targetState.path], options);

      if (navigationSucceeded) {
        // Update current state
        this._currentState.set(targetState);

        // Add to history
        this._stateHistory.update(history => [
          ...history,
          { state: stateName, timestamp: new Date(), metadata: context }
        ]);
      }

      return navigationSucceeded;

    } catch (error) {
      console.error('Navigation error:', error);
      this._error.set('Navigation failed');
      return false;
    } finally {
      this._isLoading.set(false);
    }
  }

  /**
   * Navigate to the previous state in the workflow
   * Like hitting the "back" button in a wizard
   *
   * @returns Promise<boolean> - true if navigation succeeded
   */
  public async goBack(): Promise<boolean> {
    if (!this.canGoBack()) {
      console.warn('Cannot go back - no previous state');
      return false;
    }

    const previous = this.previousState();
    if (!previous) return false;

    // Remove the last entry from history before navigating
    // (navigateToState will add a new entry)
    this._stateHistory.update(history => history.slice(0, -1));

    return this.navigateToState(previous.name);
  }

  /**
   * Reset the workflow to its initial state
   * Useful when starting over or canceling
   */
  public reset(): void {
    this._currentState.set(this.getInitialState());
    this._stateHistory.set([]);
    this._error.set(null);
    this._isLoading.set(false);
  }

  // ============================================================================
  // QUERY METHODS - ASKING QUESTIONS ABOUT THE WORKFLOW
  // ============================================================================

  /**
   * Check if we can transition from one state to another
   *
   * @param fromState - Starting state name
   * @param toState - Target state name
   * @param context - Optional context for validation
   * @returns true if transition is allowed
   */
  public canTransitionTo(fromState: string, toState: string, context?: any): boolean {
    const rule = this.getTransitionRule(fromState, toState);

    // If no rule is defined, allow the transition by default
    if (!rule) return true;

    return rule.canTransition(context);
  }

  /**
   * Get all states that can be reached from the current state
   *
   * @param context - Optional context for validation
   * @returns Array of state names that are accessible
   */
  public getAvailableTransitions(context?: any): string[] {
    const currentStateName = this._currentState()?.name;
    if (!currentStateName) return [];

    return this.getWorkflowStates()
      .map(state => state.name)
      .filter(targetState => this.canTransitionTo(currentStateName, targetState, context));
  }

  /**
   * Check if the user has visited a specific state
   *
   * @param stateName - State to check
   * @returns true if the state has been visited
   */
  public hasVisitedState(stateName: string): boolean {
    return this._stateHistory().some(entry => entry.state === stateName);
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Find a state by its name
   */
  protected getStateByName(name: string): WorkflowState | undefined {
    return this.getWorkflowStates().find(state => state.name === name);
  }

  /**
   * Find a transition rule between two states
   */
  protected getTransitionRule(fromState: string, toState: string): TransitionRule | undefined {
    return this.getTransitionRules().find(
      rule => rule.fromState === fromState && rule.toState === toState
    );
  }

  /**
   * Get the complete state history as a simple array of state names
   * Useful for debugging or analytics
   */
  public getStateHistoryNames(): string[] {
    return this._stateHistory().map(entry => entry.state);
  }
}
